import React from "react";

/*
Flat-first editor props:
- flattenedLinesAndProducts: [{ lineId: "lines/<id>", productId: "products/<id>" }]
- onAddBlankPair(): append a blank pair
- onUpdatePair(index, updates): update pair at index (updates: { lineId, productId })
- onRemovePair(index)
- onMovePair(fromIndex, toIndex)
- onAddAllProductsForLine(lineIdRef, insertAtIndex?) -> append/insert all products for that line
- availableProductsForLine(lineIdRef, rowIndex) => array of productRef strings (excludes already-used products except for current row)
- isLineSelectable(lineIdRef, rowIndex) => boolean (true if line still has any available products to pick)
- sortedLines: available production lines [{id, name}]
- productionLines: full line docs (for Add All convenience)
- getItemNameByProductRef(productRef) => display name
*/

export default function ProductionListsForm({
  flattenedLinesAndProducts = [],
  onAddBlankPair,
  onUpdatePair,
  onRemovePair,
  onMovePair,
  onAddAllProductsForLine,
  availableProductsForLine = () => [],
  isLineSelectable = () => true,
  sortedLines = [],
  productionLines = [],
  getItemNameByProductRef = () => "Item",
}) {
  // Drag/drop using HTML5 API
  function handleDragStart(e, index) {
    e.dataTransfer.setData("text/plain", String(index));
    e.dataTransfer.effectAllowed = "move";
  }

  function handleDragOver(e) {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  }

  function handleDrop(e, dropIndex) {
    e.preventDefault();
    const fromIndex = parseInt(e.dataTransfer.getData("text/plain"), 10);
    if (isNaN(fromIndex)) return;
    if (fromIndex === dropIndex) return;
    onMovePair(fromIndex, dropIndex);
  }

  function getIdFromRef(ref) {
    if (!ref) return "";
    const parts = String(ref).split("/");
    return parts.length > 1 ? parts[1] : ref;
  }

  function getLineNameFromRef(lineRef) {
    if (!lineRef) return "Line";
    const parts = String(lineRef).split("/");
    const id = parts.length > 1 ? parts[1] : lineRef;
    const lineObj = sortedLines.find((l) => l.id === id || l.id === `lines/${id}`) || productionLines.find((l) => l.id === id || l.id === `lines/${id}`);
    return lineObj ? lineObj.name || lineObj.id : id;
  }

  return (
    <div>
      <h3>Production Order (flat editor)</h3>

      <div style={{ marginBottom: 12 }}>
        <button type="button" onClick={onAddBlankPair}>
          Add Row
        </button>

        {/* quick bulk add: choose line to append all products */}
        <span style={{ marginLeft: 12 }}>
          <label style={{ marginRight: 8 }}>Bulk add all products from:</label>
          <select
            onChange={(e) => {
              const val = e.target.value;
              if (!val) return;
              onAddAllProductsForLine(`lines/${val}`, null);
              // reset select
              e.target.value = "";
            }}
            defaultValue=""
          >
            <option value="">Select line</option>
            {sortedLines.map((l) => {
              // option value should be plain id (not "lines/<id>")
              const plainId = String(l.id).split("/").pop();
              const disabled = !isLineSelectable(`lines/${plainId}`, null);
              return (
                <option key={l.id} value={plainId} disabled={disabled}>
                  {l.name}
                  {disabled ? " (all products added)" : ""}
                </option>
              );
            })}
          </select>
        </span>
      </div>

      {flattenedLinesAndProducts.length === 0 ? (
        <div style={{ color: "#666" }}>
          No rows yet — click "Add Row" or bulk-add products from a line.
        </div>
      ) : (
        <ol style={{ paddingLeft: 18 }}>
          {flattenedLinesAndProducts.map((pair, idx) => {
            const lineIdPlain = getIdFromRef(pair.lineId);
            const availableProductOptions = availableProductsForLine(pair.lineId, idx);

            return (
              <li
                key={idx}
                draggable
                onDragStart={(e) => handleDragStart(e, idx)}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, idx)}
                style={{
                  padding: "8px",
                  border: "1px solid #ddd",
                  marginBottom: 6,
                  background: "#fff",
                  display: "flex",
                  gap: 8,
                  alignItems: "center",
                }}
              >
                {/* Index on the left for clear ordering */}
                <div style={{ width: 36, textAlign: "right", fontWeight: 600 }}>
                  {idx + 1}.
                </div>

                {/* Drag handle */}
                <div style={{ width: 24, textAlign: "center", cursor: "grab" }}>
                  ≡
                </div>

                <div style={{ minWidth: 220 }}>
                  <select
                    value={lineIdPlain || ""}
                    onChange={(e) =>
                      onUpdatePair(idx, { lineId: `lines/${e.target.value}` })
                    }
                  >
                    <option value="">Select line</option>
                    {sortedLines.map((l) => {
                      // disable lines that have no remaining products (can't be selected)
                      const plainId = String(l.id).split("/").pop();
                      const disabled = !isLineSelectable(`lines/${plainId}`, idx);
                      return (
                        <option key={l.id} value={plainId} disabled={disabled}>
                          {l.name}
                          {disabled ? " (all products added)" : ""}
                        </option>
                      );
                    })}
                  </select>
                </div>

                <div style={{ minWidth: 320 }}>
                  <select
                    value={pair.productId || ""}
                    onChange={(e) =>
                      onUpdatePair(idx, { productId: e.target.value })
                    }
                    disabled={!pair.lineId}
                  >
                    <option value="">Select product</option>

                    {/* ensure we include the current product value even if it would otherwise be filtered out */}
                    {pair.productId &&
                      !availableProductOptions.includes(pair.productId) && (
                        <option value={pair.productId}>
                          {getItemNameByProductRef(pair.productId)} (current)
                        </option>
                      )}

                    {availableProductOptions.map((prodRef) => (
                      <option key={prodRef} value={prodRef}>
                        {getItemNameByProductRef(prodRef)}
                      </option>
                    ))}
                  </select>
                </div>

                <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
                  <button
                    type="button"
                    onClick={() => onMovePair(idx, Math.max(0, idx - 1))}
                    disabled={idx === 0}
                  >
                    Up
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      onMovePair(
                        idx,
                        Math.min(flattenedLinesAndProducts.length - 1, idx + 1)
                      )
                    }
                    disabled={idx === flattenedLinesAndProducts.length - 1}
                  >
                    Down
                  </button>
                  <button type="button" onClick={() => onRemovePair(idx)}>
                    Remove
                  </button>
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}